/**
 *	(c) 2024 Приволжский Исследовательский Медицинский Университет
 *
 *	@file: NavBar.js
 *  @description: Этот файл является частью клиентской стороны проекта. src Относится к части, которая содержит компоненты - части страниц, допускающие многократное использование в разных частях. NavBar.js
 *	@author: Манжос Геннадий Юрьевич,   Горбас Александр Петрович, Белов Михаил Александрович
*/
import { useContext, useEffect, useState } from 'react';
import Navbar from 'react-bootstrap/Navbar';
import { NavLink } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { Context } from '../index';
import { LanguageKeyName, DefaultLanguage, LanguagesData } from '../i18n';

import './NavBar.css'

const NavBar = observer(() => {
    const { user } = useContext(Context);
    const [ isMenuOpened, setIsMenuOpened ] = useState(false);

    //  Роль пользователя. Если её нет - то пользователь демо.
    const userRole = user.profile.role ?? 'demo';

    //  Работа с локализацией.
    const { t, i18n } = useTranslation();
    //  State с информацией о текущем языке. Объект содержит в себе всю нужную информацию - картинка, название, код...
    //  По скольку это инициализируется в файле i18n.js, то мы берём лишь значение из 'localStorage' либо по умолчанию, если такого нет.
    const [currentLanguage, setCurrentLanguage] = useState(LanguagesData.find(({ code }) => code === (localStorage.getItem(LanguageKeyName) ?? DefaultLanguage)));

    //  Смена языка.
    //  При смене языка, которое происходит, когда пользователь нажимает на кнопку-иконку вверху с названием языка,
    //  нужно обновить значение в localStorage.
    const ChangeLanguage = (event) => {
        setCurrentLanguage((current) => {
            const currentLanguageIndex = LanguagesData.findIndex(({ code }) => code === current.code);
            if (LanguagesData[currentLanguageIndex + 1] === undefined)
                return LanguagesData[0];
            else
                return LanguagesData[currentLanguageIndex + 1];
        });
    };

    //  При смене языка - отправить 'уведомление' объекту работы с локализацией о том, что язык изменился, чтобы текст на странице динамически обновился.
    useEffect(() => {
        i18n.changeLanguage(currentLanguage.code);
        localStorage.setItem(LanguageKeyName, currentLanguage.code);
        user.setLanguage(currentLanguage.code);
    }, [ currentLanguage ]);

    const toggleMenu = () => {
        setIsMenuOpened(!isMenuOpened);
    }

    const toggleSidebar = () => {
        const menuList = document.querySelector('.menu-list');
        menuList.classList.toggle('opened');
    };

    const handleSidebarLinkClick = () => {
        toggleSidebar();
        toggleMenu();
    };

    useEffect(() => {
        const newsButton = document.getElementById('newsButton');

        if (newsButton) {
            // Добавляем класс с анимацией
            newsButton.classList.add('animated');

            // Через 24 с. удаляем класс с анимацией
            const timeoutId = setTimeout(() => {
                newsButton.classList.remove('animated');
            }, 24000);

            // Очистка таймера при размонтировании компонента
            return () => clearTimeout(timeoutId);
        } 
    }, []);

    return (
        <Navbar className='navbar_block'>
            <div className='item1'>
                <a href='https://med-atlas.site' rel='noopener noreferrer'>
                    <img src={currentLanguage.image} className={ 'header_image lang_' + user.language} alt='main' />
                </a>
            </div>
            <button className='switch_language_button_navbar' onClick={ChangeLanguage}>
                <img className='icon_flag' src={currentLanguage.icon} alt={currentLanguage.code} />&nbsp;{currentLanguage.name}
            </button>
            <div className='item2'>
                <NavLink className='navbar_title' to='https://med-atlas.site/'>
                    {t('navbar_title_main')}
                </NavLink>
                <NavLink className='navbar_title' to='https://med-atlas.site/about_application'>
                    {t('navbar_title_about')}
                </NavLink>
                <NavLink className='navbar_title' to='https://med-atlas.site/support'>
                    {t('navbar_title_support')}
                </NavLink>
                <NavLink id='newsButton'  className='navbar_title news_button' title={'Последние обновления и статистика'} to='https://med-atlas.site/news'>
                    {t('navbar_title_news')}
                </NavLink>
                <NavLink className='navbar_title' to='https://med-atlas.site/testing'>{t('navbar_title_testing')}</NavLink>
                <NavLink className='navbar_title' to='https://med-atlas.site/account'>{t('navbar_title_account')}</NavLink>
                <NavLink className='navbar_title' to='https://med-atlas.site/logout'>
                    {t('navbar_title_authorization_logout')}
                </NavLink>
            </div>
            <div className='burger-menu'>
                <img className='burger-icon' onClick={toggleMenu} src='/icons/Menu@2x.png' alt='menu icon'></img>
                <div className={`menu-list ${isMenuOpened ? 'opened' : ''}`} >
                    <img className='close-btn' onClick={toggleMenu} src='/icons/Close_round_duotone@2x.png' alt='close icon'></img>
                    <div className='menu_sidebar_title'>
                        {t('navbar_title_main')}
                    </div>
                    <NavLink className='navbar_title' onClick={handleSidebarLinkClick} to='https://med-atlas.site/'>
                        <img src='/icons/Home_fill@2x.png' alt='home icon'></img>
                        {t('navbar_title_main')}
                    </NavLink>
                    <NavLink className='navbar_title' onClick={handleSidebarLinkClick} to='https://med-atlas.site/about_application'>
                        <img src='/icons/Info_fill@2x.png' alt='info icon'></img>
                        {t('navbar_title_about')}
                    </NavLink>
                    <NavLink className='navbar_title' onClick={handleSidebarLinkClick} to='https://med-atlas.site/support'>
                        <img src='/icons/Setting_fill@2x.png' alt='support icon'></img>
                        {t('navbar_title_support')}
                    </NavLink>
                    <NavLink className='navbar_title sidebar_news_button' onClick={handleSidebarLinkClick} to='https://med-atlas.site/news'>
                        <img src='/news/red-news-sidebar-icon.svg' alt='news icon' />
                        {t('navbar_title_news')}
                    </NavLink>
                    <NavLink className='navbar_title' to='https://med-atlas.site/testing' onClick={handleSidebarLinkClick}>
                        <img src='/icons/Testing_fill@2x.png' alt='testing icon' />
                        {t('navbar_title_testing')}
                    </NavLink>
                    <NavLink className='navbar_title' to='https://med-atlas.site/account' onClick={handleSidebarLinkClick}>
                        <img src='/icons/Account_menu.png' alt='account icon'></img>
                        {t('navbar_title_account')}
                    </NavLink>
                    <NavLink className='navbar_title' onClick={() => { handleSidebarLinkClick() }} to='https://med-atlas.site/logout'>
                        <img src='/icons/Sign_in_squre_fill@2x.png' alt='logout icon'></img>
                        {t('navbar_title_authorization_logout')}
                    </NavLink>
                </div>
            </div>
        </Navbar>
    );

});

export default NavBar;